/* eslint-disable prefer-arrow/prefer-arrow-functions */

import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import max from 'lodash/max';
import maxBy from 'lodash/maxBy';
import min from 'lodash/min';
import uniq from 'lodash/uniq';

import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { queryHashGenerator } from '../../../utils/queryHash';
import {
  Entity,
  EntityType,
  IAthlete,
  IComputedAggregate,
  ICrossTestMetricSelection,
  ISelectableEntity,
} from '../../types';
import {
  crossTestAggregates,
  ICrossTestAggregates,
} from '../crossTestAggregatesProvider';
import { entitiesProvider, IEntities } from '../entitiesProvider';
import {
  asymmetryReportSettingsProvider,
  IAsymmetryReportSettings,
  IAsymmetryReportSettingsState,
} from '../settings/reports/asymmetryReportSettingsProvider';
import { ISettingsTemplate } from '../settings/reports/types';
import { formatTimeframe } from '../utils/formatTimeframe';
import { isValidDateRange } from '../utils/isValidDateRange';
import { metricMask, MetricMaskKey } from '../utils/metricMask';
import { IReportState, IValidationResult } from './types';

export interface IAsymmetryReportState extends IReportState {
  settings: ISettingsTemplate<IAsymmetryReportSettings>;
  results: IAsymmetryResult[];
  entityType: EntityType;
}

export interface IAsymmetryResultMetadata {
  label: string;
  athlete?: IAthlete;
}

export enum EAsymmetryFlag {
  NONE = 'none',
  WARN = 'warn',
  CRITICAL = 'critical',
}

export interface IBaseline {
  mean: number;
  sd: number;
}

export interface IAsymmetryResultData {
  metric: ICrossTestMetricSelection;
  left: number | null;
  right: number | null;
  score: number | null;
  units: string;
  roundingPrecision: number;
  flag: EAsymmetryFlag;
  baseline: IBaseline | null;
  baselineFlag: EAsymmetryFlag;
}

export interface IAsymmetryResult {
  meta: IAsymmetryResultMetadata;
  data: IAsymmetryResultData[];
}

interface IEntityQueryArg {
  entity: Entity;
  id: string;
  type: EntityType;
}

interface ITargetData {
  data: ICrossTestAggregates;
  entity: Entity;
}

interface IEntitiesStageData extends IAsymmetryReportSettingsState {
  entityQueries: IEntityQueryArg[];
}

interface ITargetStageData extends IEntitiesStageData {
  hashes: string[];
  hashMapping: IQueryHashMapping;
  targetData: ITargetData[];
}

interface IBaselineStageData extends ITargetStageData {
  baselineData: ITargetData[];
}

interface IDataPoint {
  id: string; /// either the session id or the athlete id
  athlete?: IAthlete;
  queryHash: string;
  aggregate: IComputedAggregate;
}

interface IProcessedDataPoints {
  groupedDataPoints: Record<string, IDataPoint[]>;
  groupedBaselineDataPoints: Record<string, IDataPoint[]>;
}

export const asymmetryReportProvider = (
  entityType: EntityType,
  entity: Entity
): Observable<IAsymmetryReportState> => {
  return asymmetryReportSettingsProvider().pipe(
    /// this stage gets the individual athletes if selected entity is a group or a team
    switchMap(
      (
        settings: IAsymmetryReportSettingsState
      ): Observable<IEntitiesStageData> => {
        if (entityType === 'groups' || entityType === 'teams') {
          return entitiesProvider().pipe(
            map((entities: IEntities): IEntitiesStageData => {
              /// filter the athletes that are members of the group or team
              const athletes = entities.athletes.filter(
                (a: IAthlete): boolean => !!a?.[entityType]?.includes(entity.id)
              );
              return {
                ...settings,
                entityQueries: athletes.map(
                  (mEntity: IAthlete): IEntityQueryArg => ({
                    id: mEntity.id,
                    type: 'athletes',
                    entity: mEntity,
                  })
                ),
              };
            })
          );
        } else {
          ///  the selected entity is an athlete
          return of({
            ...settings,
            entityQueries: [{ id: entity.id, type: entityType, entity }],
          });
        }
      }
    ),
    /// this stage fetches the target data for the entities
    switchMap(
      (
        settingsAndEntities: IEntitiesStageData
      ): Observable<ITargetStageData> => {
        const settingsValidation = validateSettings(settingsAndEntities);

        if (settingsValidation.isValid) {
          if (settingsAndEntities.entityQueries.length > 0) {
            const { hashes, mapping } = createQueryHashMapping(
              settingsAndEntities.selected.settings?.metrics,
              settingsAndEntities.selected.settings?.mode?.value as
                | 'bilateral'
                | 'unilateral',
              settingsAndEntities.selected.settings.leftTag?.value,
              settingsAndEntities.selected.settings.rightTag?.value
            );

            return combineLatest(
              settingsAndEntities.entityQueries.map(
                (qe: IEntityQueryArg): Observable<ITargetData> => {
                  const { timeframe } = settingsAndEntities;

                  return crossTestAggregates(
                    hashes,
                    qe.id,
                    qe.type as EntityType,
                    timeframe.dateType,
                    timeframe.dateRange
                  ).pipe(
                    map(
                      (data: ICrossTestAggregates): ITargetData => ({
                        entity: qe.entity,
                        data,
                      })
                    )
                  );
                }
              )
            ).pipe(
              map((targetData: ITargetData[]): ITargetStageData => {
                return {
                  targetData,
                  ...settingsAndEntities,
                  hashes,
                  hashMapping: mapping,
                };
              })
            );
          } else {
            const targetStageData: ITargetStageData = {
              ...settingsAndEntities,
              hashes: null,
              targetData: [],
              hashMapping: null,
            };
            return of(targetStageData);
          }
        } else {
          return of({
            ...settingsAndEntities,
            targetData: [],
            hashes: null,
            hashMapping: null,
          });
        }
      }
    ),

    /// this stage fetches the population data
    switchMap((data: ITargetStageData): Observable<IBaselineStageData> => {
      if (!data.targetData?.length) {
        return of({
          ...data,
          baselineData: [],
          comparisonData: [],
        });
      }
      const obs = data.entityQueries.map(
        (qe: IEntityQueryArg): Observable<ITargetData> => {
          const matchedEntity = data.targetData.find(
            (td: ITargetData): boolean => td.entity.id === qe.id
          );
          const afterData =
            data?.timeframe?.dateType?.value === 'all-time'
              ? null
              : matchedEntity.data;

          return crossTestAggregates(
            data.hashes ?? [],
            qe.id,
            qe.type as EntityType,
            data.baselineTimeframe.dateType,
            data.baselineTimeframe.dateRange,
            afterData
          ).pipe(
            map(
              (mData: ICrossTestAggregates): ITargetData => ({
                entity: qe.entity,
                data: mData,
              })
            )
          );
        }
      );
      if (obs.length > 0) {
        return combineLatest(obs).pipe(
          map((evt: ITargetData[]): IBaselineStageData => {
            return {
              ...data,
              baselineData: evt,
            };
          })
        );
      } else {
        return of({
          ...data,
          baselineData: [],
        });
      }
    }),

    /// this stage creates the finalized report data
    map((completeData: IBaselineStageData): IAsymmetryReportState => {
      const { timeframe, selected } = completeData;

      const mTimeframe =
        timeframe?.dateType?.value === 'custom'
          ? formatTimeframe(
              timeframe?.dateRange?.from?.value,
              timeframe?.dateRange?.to?.value
            )
          : timeframe?.dateType?.label;

      /// validate settings
      const settingsValidation = validateSettings(completeData);

      if (!settingsValidation.isValid) {
        return {
          ...settingsValidation,
          settings: selected,
          timeframe: mTimeframe,
          results: [],
          entityType,
        };
      }

      const results = makeAsymmetryResults(completeData, entityType);
      /// validate data
      const dataValidation = validateData(results);

      return {
        settings: selected,
        timeframe: mTimeframe,
        ...dataValidation,
        results,
        entityType,
      };
    })
  );
};

interface IQueryHashMapping {
  [leftHash: string]: {
    rightHash: string;
    testTypeId: string;
    baseTags: string[];
    leftTag: string;
    rightTag: string;
  };
}

const createQueryHashMapping = (
  metrics: ICrossTestMetricSelection[],
  mode: 'bilateral' | 'unilateral',
  leftTag?: string,
  rightTag?: string
): { hashes: string[]; mapping: IQueryHashMapping } => {
  const hashes: string[] = [];
  const mapping: IQueryHashMapping = {};
  for (const metric of metrics) {
    const tags = metric.tags.map((tag: ISelectableEntity): string => tag.value);
    if (mode === 'bilateral') {
      const hash = queryHashGenerator(metric.testType.value, tags);
      hashes.push(hash);
    } else {
      const leftHash = queryHashGenerator(metric.testType.value, [
        ...tags,
        leftTag,
      ]);
      const rightHash = queryHashGenerator(metric.testType.value, [
        ...tags,
        rightTag,
      ]);
      hashes.push(leftHash, rightHash);
      mapping[leftHash] = {
        rightHash,
        testTypeId: metric.testType.value,
        baseTags: tags,
        leftTag,
        rightTag,
      };
    }
  }

  return { hashes: uniq(hashes), mapping };
};

/**
 * @description Creates the asymmetry results from baseline stage data
 * @param data The baseline stage data containing target and baseline information
 * @param entityType The type of entity (athletes, teams, etc.)
 * @returns Array of asymmetry results
 */
function makeAsymmetryResults(
  data: IBaselineStageData,
  entityType: EntityType
): IAsymmetryResult[] {
  const metricIds = extractMetricIds(data.selected.settings.metrics);
  const mode = data.selected.settings.mode.value;

  // Process target and baseline data points
  const processedData = processDataPoints(data, entityType, metricIds);
  const { groupedDataPoints, groupedBaselineDataPoints } = processedData;

  return processResults(
    groupedDataPoints,
    groupedBaselineDataPoints,
    mode,
    entityType,
    data.selected.settings.metrics,
    data.hashMapping
  );
}

/**
 * @description Extracts metric IDs from the metric selection array
 * @param metrics Array of cross test metric selections
 * @returns Array of metric mask keys
 */
function extractMetricIds(
  metrics: ICrossTestMetricSelection[]
): MetricMaskKey[] {
  return metrics.map(
    (m: ICrossTestMetricSelection): MetricMaskKey =>
      `${m.testType.value}_${m.metric.value}`
  );
}

/**
 * @description Processes target and baseline data points, grouping them by ID
 * @param data The baseline stage data containing target and baseline information
 * @param entityType The type of entity (athletes, teams, etc.)
 * @param metricIds Array of metric identifiers to process
 * @returns Object containing grouped data points for both target and baseline data
 */
function processDataPoints(
  data: IBaselineStageData,
  entityType: EntityType,
  metricIds: MetricMaskKey[]
): IProcessedDataPoints {
  const dataPoints = flatMap(
    data.targetData,
    (target: ITargetData): IDataPoint[] =>
      extractDataPoints(
        target.data,
        entityType === 'athletes' ? 'sessions' : 'combined',
        metricIds,
        entityType === 'athletes' ? null : target.entity
      )
  );

  const baselineDataPoints = flatMap(
    data.baselineData,
    (target: ITargetData): IDataPoint[] =>
      extractDataPoints(
        target.data,
        'combined',
        metricIds,
        entityType === 'athletes' ? null : target.entity
      )
  );

  return {
    groupedDataPoints: groupBy(dataPoints, 'id'),
    groupedBaselineDataPoints: groupBy(baselineDataPoints, 'id'),
  };
}

/**
 * @description Processes all results data into a final array of asymmetry results
 * @param groupedDataPoints Record of data points grouped by ID
 * @param groupedBaselineDataPoints Record of baseline data points grouped by ID
 * @param mode The processing mode (bilateral or unilateral)
 * @param entityType The type of entity (athletes, teams, etc.)
 * @param metrics Array of cross test metric selections
 * @param hashMapping Mapping of left/right query hashes
 * @returns Array of asymmetry results
 */
function processResults(
  groupedDataPoints: Record<string, IDataPoint[]>,
  groupedBaselineDataPoints: Record<string, IDataPoint[]>,
  mode: 'bilateral' | 'unilateral',
  entityType: EntityType,
  metrics: ICrossTestMetricSelection[],
  hashMapping: IQueryHashMapping
): IAsymmetryResult[] {
  const results: IAsymmetryResult[] = [];

  for (const [id, dataPointGroup] of Object.entries(groupedDataPoints)) {
    const { label, athlete } = extractMetadata(dataPointGroup);

    if (mode === 'bilateral') {
      const result = processBilateralResult({
        dataPointGroup,
        groupedBaselineDataPoints,
        id,
        entityType,
        metrics,
        label,
        athlete,
      });
      /// only include results with data
      if (result.data.length > 0) {
        results.push(result);
      }
    } else {
      const result = processUnilateralResult({
        dataPointGroup,
        groupedBaselineDataPoints,
        id,
        entityType,
        hashMapping,
        metrics,
        label,
        athlete,
      });
      /// only include results with data
      if (result.data.length > 0) {
        results.push(result);
      }
    }
  }

  return results;
}

/**
 * @description Extracts metadata (label and athlete) from a group of data points
 * @param dataPointGroup Array of data points
 * @returns Object containing label and athlete information
 */
function extractMetadata(dataPointGroup: IDataPoint[]): {
  label: string | undefined;
  athlete: IAthlete | undefined;
} {
  return {
    label:
      dataPointGroup[0]?.athlete?.name ??
      dataPointGroup[0]?.aggregate?.meta?.id,
    athlete: dataPointGroup[0]?.athlete,
  };
}

interface BilateralProcessingParams {
  dataPointGroup: IDataPoint[];
  groupedBaselineDataPoints: Record<string, IDataPoint[]>;
  id: string;
  entityType: EntityType;
  metrics: ICrossTestMetricSelection[];
  label: string;
  athlete?: IAthlete;
}

/**
 * @description Processes bilateral results for a group of data points
 */
function processBilateralResult({
  dataPointGroup,
  groupedBaselineDataPoints,
  id,
  entityType,
  metrics,
  label,
  athlete,
}: BilateralProcessingParams): IAsymmetryResult {
  const baselineDataPointGroup = getBaselineDataPoints(
    entityType,
    groupedBaselineDataPoints,
    id
  );

  const pairs = createBilateralMetricPairs(dataPointGroup, metrics);
  const baselineAggregates = createBilateralMetricPairs(
    baselineDataPointGroup,
    metrics
  );

  return toAsymmetryResult(
    pairs,
    baselineAggregates,
    'bilateral',
    metrics,
    label,
    athlete
  );
}

interface UnilateralProcessingParams {
  dataPointGroup: IDataPoint[];
  groupedBaselineDataPoints: Record<string, IDataPoint[]>;
  id: string;
  entityType: EntityType;
  hashMapping: IQueryHashMapping;
  metrics: ICrossTestMetricSelection[];
  label: string;
  athlete?: IAthlete;
}

/**
 * @description Processes unilateral results for a group of data points
 */
function processUnilateralResult({
  dataPointGroup,
  groupedBaselineDataPoints,
  id,
  entityType,
  hashMapping,
  metrics,
  label,
  athlete,
}: UnilateralProcessingParams): IAsymmetryResult {
  const targetPairs = createUnilateralMetricPairs(dataPointGroup, hashMapping);

  const baselinePairs = createUnilateralMetricPairs(
    getBaselineDataPoints(entityType, groupedBaselineDataPoints, id),
    hashMapping
  );

  return toAsymmetryResult(
    targetPairs,
    baselinePairs,
    'unilateral',
    metrics,
    label,
    athlete
  );
}

/**
 * @description extracts the nested data points from the aggregates into a flat array
 * @param aggregates
 * @param dataKey
 * @param metricIds
 * @param entity
 * @returns
 */
const extractDataPoints = (
  aggregates: ICrossTestAggregates,
  dataKey: 'sessions' | 'combined',
  metricIds: MetricMaskKey[],
  entity?: Entity
): IDataPoint[] => {
  const dataPoints: IDataPoint[] = [];

  for (const [hash, aggregate] of Object.entries(aggregates)) {
    const data =
      dataKey === 'sessions' ? aggregate[dataKey] : [aggregate[dataKey]];
    if (!isEmpty(data)) {
      for (const session of data) {
        if (!isEmpty(session?.data)) {
          const computed = metricMask(session, metricIds);
          if (!isEmpty(computed?.data)) {
            dataPoints.push({
              id: entity ? entity.id : session.meta.id,
              queryHash: hash,
              aggregate: computed,
              athlete: entity ? (entity as IAthlete) : null,
            });
          }
        }
      }
    }
  }
  return dataPoints;
};

/**
 * @description Validates the presence of results data
 * @param results Array of asymmetry results to validate
 * @returns Validation result indicating if data is sufficient
 */
function validateData(results: IAsymmetryResult[]): IValidationResult {
  if (results.length > 0) {
    return {
      isValid: true,
    };
  } else {
    return {
      isValid: false,
      errorCode: 'insufficient-data',
    };
  }
}

/**
 * @description Validates the user settings for the report
 * @param settings The asymmetry report settings state to validate
 * @returns Validation result indicating if settings are valid
 */
function validateSettings(
  settings: IAsymmetryReportSettingsState
): IValidationResult {
  if (!settings?.selected) {
    return {
      isValid: false,
      errorCode: 'missing-template',
    };
  } else if (!isValidDateRange(settings?.timeframe)) {
    return {
      isValid: false,
      errorCode: 'invalid-date-range',
    };
  } else if (!isValidDateRange(settings?.baselineTimeframe)) {
    return {
      isValid: false,
      errorCode: 'invalid-baseline-date-range',
    };
  }

  return { isValid: true };
}

/**
 * @description Calculates the asymmetry score for pairs of aggregates from unilateral testing
 * @param pair Array of computed aggregates for left and right measurements
 * @param metrics Array of cross test metric selections
 * @param baselinePair Array of baseline computed aggregates
 * @returns Array of asymmetry result data
 */
function unilateralAsymmetryResult(
  pair: IComputedAggregate[],
  metrics: ICrossTestMetricSelection[],
  baselinePair: IComputedAggregate[]
): IAsymmetryResultData[] {
  const scoredResults: IAsymmetryResultData[] = [];

  /// figure out who has more keys in the pair and then extract the metric ids from that pair
  /// in case one result has more keys than the other
  const sample = maxBy(
    pair,
    (p: IComputedAggregate): number => Object.keys(p?.data ?? {}).length
  );

  const matchingQueryHashes = pair
    .filter((p: IComputedAggregate): boolean => Boolean(p?.meta))
    .map((p: IComputedAggregate): string =>
      queryHashGenerator(
        p.meta.testType,
        p.meta.asymmetryMapping?.baseTags ?? []
      )
    );

  const metricIds = Object.keys(sample.data);

  /// there should be a result for every metric
  for (const metricId of metricIds) {
    const metric = metrics.find((m: ICrossTestMetricSelection): boolean => {
      return (
        matchingQueryHashes.includes(m.queryHash) && m.metric.value === metricId
      );
    });

    if (!metric) {
      continue;
    }

    const warningLevel = metric.changeCutoffs?.lowerCutoff;
    const criticalLevel = metric.changeCutoffs?.upperCutoff;

    /// seperate left and right
    const left = pair[0]?.data?.[metricId];
    const right = pair[1]?.data?.[metricId];
    /// include the units and rounding precision for display
    const units = left?.units;
    const roundingPrecision = left?.roundingPrecision;

    // sets the correct divisor in the asymmetry equation, depending on metric directionality
    const setDenom = (left: number, right: number) => {
      switch (metric.metric.entity.direction) {
        case 1:
          return right < 0 ? -max([left, right]) : max([left, right]);
        case -1:
          return left < 0 ? min([left, right]) : -min([left, right]);
        case 0:
          return min([Math.abs(left), Math.abs(right)]);
        default:
          break;
      }
    };

    if (left !== undefined && right !== undefined) {
      // determines the denominator value to use in the following equation
      const denom = setDenom(left.mean, right.mean);

      const value = ((left.mean - right.mean) / denom) * 100;

      const baselineLeft = baselinePair?.[0]?.data?.[metricId];
      const baselineRight = baselinePair?.[1]?.data?.[metricId];

      let baselineValue = null;
      let baselineSd = null;
      if (baselineLeft && baselineRight) {
        const baseDenom = setDenom(baselineLeft.mean, baselineRight.mean);
        // Unilateral asymmetry equation
        baselineValue =
          ((baselineLeft.mean - baselineRight.mean) / baseDenom) * 100;
        baselineSd = Math.sqrt(
          (baselineLeft.populationSd ** 2 + baselineRight.populationSd ** 2) / 2
        );
      }
      scoredResults.push({
        metric,
        left: left.mean,
        right: right.mean,
        score: value,
        flag: getFlag(value, warningLevel, criticalLevel),
        units,
        roundingPrecision,
        baseline: {
          mean: baselineValue,
          sd: baselineSd,
        },
        baselineFlag: getFlag(baselineValue, warningLevel, criticalLevel),
      });
    }
  }
  return scoredResults;
}

/**
 * @description Calculates the asymmetry score for a list of aggregates from bilateral testing
 * @param items Array of computed aggregates from bilateral testing
 * @param metrics Array of cross test metric selections
 * @param baselineItems Array of baseline computed aggregates
 * @returns Array of asymmetry result data
 */
function bilateralAsymmetryResult(
  items: IComputedAggregate[],
  metric: ICrossTestMetricSelection,
  baselineItems: IComputedAggregate[]
): IAsymmetryResultData[] {
  const scoredResults: IAsymmetryResultData[] = [];
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < items.length; i++) {
    const item = items?.[i];

    if (item) {
      const baselineItem = baselineItems
        /// filter out any null values
        ?.filter((x: IComputedAggregate): boolean => Boolean(x))
        .find(
          (b: IComputedAggregate): boolean =>
            queryHashGenerator(b.meta.testType, b.meta.tags) ===
            queryHashGenerator(item.meta.testType, item.meta.tags)
        );

      const metricId = metric.metric.value;

      const baselineValue = baselineItem?.data?.[metricId]?.mean;
      const baselineSd = baselineItem?.data?.[metricId]?.populationSd;

      if (metricId in item?.data) {
        const value = item?.data?.[metricId]?.mean;
        /// include the units and rounding precision for display
        const units = item?.data?.[metricId]?.units;
        const roundingPrecision = item?.data?.[metricId]?.roundingPrecision;
        const warningLevel = metric.changeCutoffs?.lowerCutoff;
        const criticalLevel = metric.changeCutoffs?.upperCutoff;
        scoredResults.push({
          metric,
          left: null,
          right: null,
          score: value,
          units,
          roundingPrecision,
          flag: getFlag(value, warningLevel, criticalLevel),
          baseline: {
            mean: baselineValue,
            sd: baselineSd,
          },
          baselineFlag: getFlag(baselineValue, warningLevel, criticalLevel),
        });
      }
    }
  }
  return scoredResults;
}

/**
 * @description converts a data point to an asymmetry result
 * @param pairs
 * @param mode
 * @param metrics
 * @param label
 * @param athlete
 * @returns
 */
function toAsymmetryResult(
  pairs: IComputedAggregate[][],
  baselinePairs: IComputedAggregate[][],
  mode: 'bilateral' | 'unilateral',
  metrics: ICrossTestMetricSelection[],
  label: string,
  athlete?: IAthlete
): IAsymmetryResult {
  const scoredResults: IAsymmetryResultData[] = [];
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i];
    const baselinePair = baselinePairs[i];

    switch (mode) {
      case 'unilateral':
        scoredResults.push(
          ...unilateralAsymmetryResult(pair, metrics, baselinePair)
        );
        break;
      case 'bilateral':
        scoredResults.push(
          ...bilateralAsymmetryResult(pair, metrics[i], baselinePair)
        );
        break;
    }
  }

  return {
    meta: {
      /// the label will either be the athlete's name if supplied or the date of the session
      label,
      /// the athlete is only supplied if the selected entity is a group or a team
      athlete,
    },
    data: scoredResults,
  };
}

/**
 * @description Gets the flag level for an asymmetry score based on warning and critical thresholds
 * @param score The asymmetry score to evaluate
 * @param warningLevel The threshold for warning level
 * @param criticalLevel The threshold for critical level
 * @returns The appropriate asymmetry flag level
 */
function getFlag(
  score: number | undefined,
  warningLevel: number,
  criticalLevel: number
): EAsymmetryFlag {
  if (score == null) {
    return EAsymmetryFlag.NONE;
  }
  switch (true) {
    case Math.abs(score) >= criticalLevel:
      return EAsymmetryFlag.CRITICAL;
    case Math.abs(score) >= warningLevel:
      return EAsymmetryFlag.WARN;
    default:
      return EAsymmetryFlag.NONE;
  }
}

/**
 * @description Gets the baseline data points based on entity type
 * @param entityType The type of entity (athletes, teams, etc.)
 * @param groupedBaselineDataPoints Record of baseline data points grouped by ID
 * @param id The identifier for the current group
 * @returns Array of baseline data points
 */
function getBaselineDataPoints(
  entityType: EntityType,
  groupedBaselineDataPoints: Record<string, IDataPoint[]>,
  id: string
): IDataPoint[] {
  return entityType === 'athletes'
    ? Object.values(groupedBaselineDataPoints).flat()
    : groupedBaselineDataPoints[id] ?? [];
}

/**
 * @description Creates pairs of left and right data points using hash mapping
 * @param dataPoints Array of data points to pair
 * @param hashMapping Mapping of left/right query hashes
 * @returns Array of paired computed aggregates
 */
function createUnilateralMetricPairs(
  dataPoints: IDataPoint[],
  hashMapping: IQueryHashMapping
): IComputedAggregate[][] {
  return dataPoints.reduce(
    (acc: IComputedAggregate[][], dp: IDataPoint): IComputedAggregate[][] => {
      const leftHash = dp.queryHash;
      const mapping = hashMapping[leftHash];

      // Only process if this is a left-side metric
      if (mapping) {
        const rightDataPoint = dataPoints.find(
          (odp: IDataPoint): boolean => odp.queryHash === mapping.rightHash
        );

        // Create enhanced aggregates with mapping metadata
        const leftAggregate: IComputedAggregate = {
          ...dp.aggregate,
          meta: {
            ...dp.aggregate.meta,
            asymmetryMapping: {
              side: 'left',
              ...mapping,
            },
          },
        };

        const rightAggregate: IComputedAggregate | null =
          rightDataPoint?.aggregate
            ? {
                ...rightDataPoint.aggregate,
                meta: {
                  ...rightDataPoint.aggregate.meta,
                  asymmetryMapping: {
                    side: 'right',
                    ...mapping,
                  },
                },
              }
            : null;

        acc.push([leftAggregate, rightAggregate]);
      }
      return acc;
    },
    []
  );
}

/**
 * @description
 * @param dataPoints Array of data points to pair
 * @returns Array of paired computed aggregates
 */
function createBilateralMetricPairs(
  dataPoints: IDataPoint[],
  metrics: ICrossTestMetricSelection[]
): IComputedAggregate[][] {
  const pairs: IComputedAggregate[][] = [];
  for (const metric of metrics) {
    const dataPoint = dataPoints.find(
      (dp: IDataPoint): boolean =>
        dp.queryHash ===
        queryHashGenerator(
          metric.testType.value,
          metric.tags.map((m: ISelectableEntity): string => m.value)
        )
    );
    if (dataPoint) {
      pairs.push([dataPoint.aggregate]);
    } else {
      pairs.push([null]);
    }
  }
  return pairs;
}
